import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Loader2, AlertCircle, AlertTriangle, CheckCircle2, Plus, Trash2,
  KeyRound, Package, ListChecks, Calendar, Ban, ShieldOff, UserX, UserCheck,
  Info, Sparkles, HelpCircle, Mail, Pencil, ArrowRight, Unlink, UserPen, Undo2,
} from 'lucide-react';
import { Button, Avatar } from '../ui-v2';
import { Modal, Badge, Input } from '../ui-v2.5';
import PantallaProceso from './PantallaProceso';
import {
  BarraAcciones, FranjaFina, BandaTotal, CabeceraColumna, EstadoTarea, QuienSeEntera,
} from './piezasProceso';
import { RUTA_BAJAS } from './rutasProceso';
import { parecidos } from './parecidoATarea';
import {
  getBaja, cambiarEstadoTareaBaja, crearTareaBaja, eliminarTareaBaja, crearTareaMaterialBaja,
  revocarCredencialBaja, revocarCredencialDeducidaBaja, deshacerRevocacionBaja, cierrePrevioBaja, cerrarBaja,
  editarDestinatariosBaja, renotificarBaja, listAreas,
} from './api';
import { fmtFecha, fmtMomento } from './fecha';
import EditarBajaDialog from './EditarBajaDialog';

/**
 * DetalleBaja — el seguimiento de una salida (Fase 11 §4.6), con PANTALLA PROPIA desde el 14/10.
 *
 * Hasta entonces era `DetalleBajaDrawer`, un panel lateral encima del listado. Ahora lo enmarca
 * `PantallaProceso` en la ruta `…/bajas/:id` (ver `BajaPage.jsx`), con el MISMO contenido: el
 * rediseño es otra pieza (FASE_PANTALLA_DE_ALTA_Y_BAJA.md, §6).
 *
 * Gemelo del de altas en estructura —cabecera de marca, carga con bandera de cancelación, tres
 * estados— y distinto en lo que importa:
 *
 *  - **La cabecera dice si la persona TIENE ACCESO o no**, que es el dato que define una baja.
 *    En el de altas eso no existe porque nadie se queda fuera al incorporarse.
 *  - **El cierre ya no desactiva** (§2.ter): la desactivación va con la fecha. La confirmación
 *    tiene que decir la verdad de cada caso, y son tres distintos — ver `ConfirmarCierre`.
 *  - **El progreso viene del backend** en cada respuesta, igual que en altas: dos sitios
 *    calculando lo mismo es la forma segura de que un día discrepen.
 *
 * Sin componentes nuevos de ui-v2.5, sin `ToastContainer` local, y sólo tokens que existen en
 * tailwind.config (`v25.ink` es 900/700/500/400/300: no hay 600 ni 800).
 */

const cuerpoError = (e) => (e && e.response && e.response.data) || {};

const ESTADO_BAJA_MAP = {
  borrador: { tone: 'waiting', label: 'Borrador' },
  en_curso: { tone: 'progress', label: 'En curso' },
  completada: { tone: 'active', label: 'Completada' },
  cancelada: { tone: 'expired', label: 'Cancelada' },
};

// El clic CICLA por los tres estados, como en el panel de altas: pendiente → en curso →
// completada → pendiente. Un checkbox sólo tendría dos, y "en curso" es información real —
// alguien ya está detrás de ese portátil.
const CICLO = { pendiente: 'en_curso', en_curso: 'completada', completada: 'pendiente' };
function Vacio({ children }) {
  return <p className="rounded-v25-md border border-dashed border-v25-line px-3 py-2.5 text-xs text-v25-ink-400">{children}</p>;
}

/**
 * La confirmación de cierre. **Tres mensajes distintos, porque son tres situaciones distintas**, y
 * el que más se repetía en el diseño anterior —"la persona quedará desactivada"— ya casi nunca es
 * cierto: desde §2.ter la desactivación va con la fecha, no con el cierre.
 *
 *   · ya desactivada         → cerrar sólo da por terminado el papeleo
 *   · fecha llegada y activa → **este cierre sí la desactivará** (el cron no ha pasado)
 *   · fecha futura           → no se desactiva a nadie; se desactivará sola al llegar la fecha
 *
 * Decir siempre lo mismo habría sido más fácil de escribir y habría mentido en dos de los tres.
 */
function ConfirmarCierre({ previo, titular, enVuelo, onClose, onConfirmar }) {
  const [confFecha, setConfFecha] = useState(false);
  const [confTareas, setConfTareas] = useState(false);

  const pendientes = previo?.tareas_pendientes || [];
  const deps = previo?.dependencias || {};
  const futura = !!previo?.fecha_futura;
  const yaSinAcceso = titular && titular.activo === false;
  const desactivaraAhora = !futura && !yaSinAcceso;

  const faltaConfirmar = (futura && !confFecha) || (pendientes.length > 0 && !confTareas);

  const nombre = titular?.nombre || 'Esta persona';
  const listaNombres = (arr) => arr.map((x) => x.nombre).join(', ');

  return (
    <Modal
      open
      tone="danger"
      size="md"
      onClose={enVuelo ? undefined : onClose}
      title={`¿Cerrar ${previo?.baja?.numero || 'la baja'}?`}
      subtitle={`${nombre}${previo?.baja?.fecha_baja ? ` · fecha de baja ${fmtFecha(previo.baja.fecha_baja)}` : ''}`}
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={enVuelo}>Volver</Button>
          <Button onClick={() => onConfirmar({
            confirmar_fecha_futura: confFecha, confirmar_tareas_pendientes: confTareas,
          })} disabled={enVuelo || faltaConfirmar}>
            {enVuelo ? <Loader2 className="animate-spin" /> : <CheckCircle2 />}Cerrar la baja
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        {/* Qué pasa con su acceso. Lo primero, porque es lo que no se puede deshacer de un clic. */}
        {desactivaraAhora ? (
          <div className="flex gap-2 rounded-v25-lg border border-red-200 bg-red-50 px-3 py-2.5">
            <UserX className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
            <p className="text-xs leading-relaxed text-red-800">
              <strong className="font-semibold">{nombre} perderá el acceso al cerrar.</strong> Su fecha
              de baja ya pasó y todavía está activa, así que este cierre la desactiva.
            </p>
          </div>
        ) : futura ? (
          <div className="flex gap-2 rounded-v25-lg border border-v25-line bg-v25-info-bg px-3 py-2.5">
            <Calendar className="mt-0.5 h-4 w-4 shrink-0 text-v25-info-fg" />
            <p className="text-xs leading-relaxed text-v25-ink-700">
              La fecha de baja <strong className="font-semibold">aún no ha llegado</strong>. Cerrar ahora
              sólo da por terminado el papeleo: {nombre} sigue trabajando y su cuenta se desactivará
              sola el {fmtFecha(previo.baja.fecha_baja)}.
            </p>
          </div>
        ) : (
          <div className="flex gap-2 rounded-v25-lg border border-v25-line bg-v25-surface-sunken px-3 py-2.5">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-v25-ink-400" />
            <p className="text-xs leading-relaxed text-v25-ink-700">
              {nombre} ya está sin acceso desde su fecha de baja. Cerrar sólo da por terminado el
              proceso; no se toca nada suyo.
            </p>
          </div>
        )}

        {/* Lo que queda por hacer, ENUMERADO. Contarlas no sirve: "2 tareas" no se puede accionar. */}
        {pendientes.length > 0 && (
          <div className="rounded-v25-lg border border-amber-200 bg-amber-50 px-3 py-2.5">
            <p className="text-xs font-semibold text-amber-900">
              Quedan {pendientes.length} tarea{pendientes.length === 1 ? '' : 's'} sin completar
            </p>
            <ul className="mt-1 list-disc space-y-0.5 pl-4 text-xs text-amber-800">
              {pendientes.map((t) => <li key={t.id}>{t.descripcion}</li>)}
            </ul>
            <label className="mt-2 flex cursor-pointer items-start gap-2 text-xs text-amber-900">
              <input type="checkbox" className="mt-0.5" checked={confTareas} onChange={(e) => setConfTareas(e.target.checked)} />
              <span>Ya no aplican. Cerrar igualmente.</span>
            </label>
          </div>
        )}

        {/* Las DEPENDENCIAS: se enumeran, nunca bloquean. Una persona se va tenga lo que tenga. */}
        {(deps.supervisa_a?.length || deps.supervisada_por?.length || deps.areas?.length
          || deps.vacaciones?.length || deps.gestion_vacaciones) ? (
            <div className="rounded-v25-lg border border-v25-line px-3 py-2.5">
              <p className="text-xs font-semibold text-v25-ink-700">Queda pendiente de reasignar</p>
              <ul className="mt-1 space-y-0.5 text-xs text-v25-ink-500">
                {deps.supervisa_a?.length > 0 && <li><strong className="font-medium">Supervisaba a:</strong> {listaNombres(deps.supervisa_a)}</li>}
                {deps.supervisada_por?.length > 0 && <li><strong className="font-medium">Su responsable:</strong> {listaNombres(deps.supervisada_por)}</li>}
                {deps.areas?.length > 0 && <li><strong className="font-medium">Miembro de:</strong> {listaNombres(deps.areas)}</li>}
                {deps.vacaciones?.length > 0 && <li><strong className="font-medium">Vacaciones sin resolver:</strong> {deps.vacaciones.length} solicitud(es)</li>}
              </ul>
              {/* El ámbito de vacaciones va DESTACADO y aparte: es el único que no aparece en
                  ninguna ficha, así que si se pierde aquí no lo ve nadie hasta que alguien pida
                  vacaciones y no haya quien se las apruebe. */}
              {deps.gestion_vacaciones?.es_el_unico && (
                <div className="mt-2 border-l-4 border-amber-500 bg-amber-50 px-3 py-2">
                  <p className="text-xs font-semibold text-amber-900">
                    Era la única persona que gestionaba su ámbito de vacaciones.
                  </p>
                  <p className="mt-0.5 text-xs text-amber-800">
                    Nadie más puede aprobar esas solicitudes. Hay que asignar a otra persona.
                  </p>
                </div>
              )}
            </div>
          ) : null}

        {futura && (
          <label className="flex cursor-pointer items-start gap-2 text-xs text-v25-ink-700">
            <input type="checkbox" className="mt-0.5" checked={confFecha} onChange={(e) => setConfFecha(e.target.checked)} />
            <span>Entiendo que la fecha aún no ha llegado y quiero cerrar el proceso igualmente.</span>
          </label>
        )}
      </div>
    </Modal>
  );
}

export default function DetalleBaja({ bajaId, onCambio }) {
  const [d, setD] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(false);
  const [enVuelo, setEnVuelo] = useState(null);      // clave de la acción en curso
  // Catálogo de áreas para el desplegable, y el área seleccionada en él.
  const [areasCatalogo, setAreasCatalogo] = useState([]);
  const [areaAAvisar, setAreaAAvisar] = useState('');
  // El correo que se está escribiendo, y el error de forma si lo hay.
  const [correoAAvisar, setCorreoAAvisar] = useState('');
  const [errorCorreo, setErrorCorreo] = useState(null);
  // Confirmación del reenvío: se abre con los NÚMEROS delante, no se manda a ciegas.
  const [confReenvio, setConfReenvio] = useState(null);
  const [nuevaTarea, setNuevaTarea] = useState('');
  const [previo, setPrevio] = useState(null);        // datos de la confirmación de cierre
  const [cargandoPrevio, setCargandoPrevio] = useState(false);
  const [editarAbierto, setEditarAbierto] = useState(false);   // el diálogo de fecha y motivo

  const cargar = useCallback(async () => {
    setCargando(true); setError(false);
    try {
      setD(await getBaja(bajaId));
    } catch (e) {
      console.error('[bajas][detalle] cargar', e);
      // 404 es «no existe»: con la URL a la vista, eso se dice distinto de «falló».
      setError(e?.response?.status === 404 ? 'no_existe' : 'fallo');
    } finally {
      setCargando(false);
    }
  }, [bajaId]);

  useEffect(() => { cargar(); }, [cargar]);

  /* El catalogo de areas para el desplegable. Se pide SIN condicionarlo a que se abra ningun
   * formulario: en Fase C el selector de altas salia con una sola opcion porque la llamada estaba
   * detras de un `if`, y el sintoma era «no hay areas» cuando la causa era «no se han pedido». */
  useEffect(() => {
    listAreas()
      .then((r) => setAreasCatalogo((Array.isArray(r) ? r : r.areas || []).filter((a) => a.activa !== false)))
      /* No se traga el fallo: sin areas no se puede anadir un destinatario, y un desplegable
       * vacio por un 500 se lee igual que «no hay areas». El toast lo distingue. */
      .catch((e) => {
        setAreasCatalogo([]);
        toast.error((e && e.response && e.response.data && e.response.data.message)
          || 'No se ha podido cargar el catálogo de áreas.');
      });
  }, []);

  // Toda acción recarga el detalle y avisa al listado. Recargar entero en vez de parchear el
  // estado local: el backend recalcula progreso y derivados, y mantener una copia en paralelo es
  // justo la manera de que las dos versiones acaben discrepando.
  const trasAccion = async (mensaje) => {
    await cargar();
    if (mensaje) toast.success(mensaje);
    if (onCambio) onCambio();
  };

  const accion = async (clave, fn, mensaje) => {
    setEnVuelo(clave);
    try {
      await fn();
      await trasAccion(mensaje);
    } catch (e) {
      toast.error(cuerpoError(e).message || 'No se pudo completar la acción.');
    } finally {
      setEnVuelo(null);
    }
  };

  /* Cambiar la lista NO envia nada: son dos actos. Asi se ajusta varias veces y se avisa una. */
  /* Se manda SIEMPRE la lista completa —áreas y correos juntos—, no la pieza. Es un conjunto, y
   * con dos llamadas un fallo entre medias dejaría una lista que nadie pidió. */
  const listaActual = () => [
    ...((d.areas_elegidas || []).map((x) => ({ area_id: x.id }))),
    ...((d.correos_elegidos || []).map((x) => ({ email: x }))),
  ];

  const guardarDestinatarios = async (lista) => {
    await accion('areas', () => editarDestinatariosBaja(d.id, lista), 'Destinatarios actualizados.');
    setAreaAAvisar('');
    setCorreoAAvisar('');
    setErrorCorreo(null);
  };

  /* La forma del correo se comprueba TAMBIÉN aquí, además de en el servidor. No para confiar en
   * ello —el servidor valida igual— sino porque un correo mal escrito no da error al enviar:
   * simplemente no llega. Avisar en el momento es la única forma de que alguien se entere. */
  const anadirCorreo = async () => {
    const v = correoAAvisar.trim().toLowerCase();
    if (!v) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
      setErrorCorreo('No parece una dirección de correo.');
      return;
    }
    if ((d.correos_elegidos || []).some((x) => String(x).toLowerCase() === v)) {
      setErrorCorreo('Ese correo ya está en la lista.');
      return;
    }
    await guardarDestinatarios([...listaActual(), { email: v }]);
  };

  /* El reenvio se abre con los NUMEROS delante: a cuantos se va a escribir y cuantos ya lo
   * recibieron, calculados con lo que el detalle YA trae. No se llama al servidor para componer
   * una frase. */
  /* SE CUENTAN PERSONAS, NO AREAS. La version anterior sumaba `areas_derivadas.length +
   * areas_elegidas.length` y lo enseñaba como destinatarios. En BAJ-2026-0002 eso dijo «1 area»
   * cuando esa area (Sistemas) **no tenia a nadie**, y ademas **no contaba los correos sueltos** —
   * el unico destinatario real de esa baja no aparecia en el numero.
   *
   * Un numero que cuenta otra cosa es peor que ningun numero: se lee como una confirmacion. Se
   * cuenta como en altas: personas distintas de todas las areas (sin repetir a quien este en dos)
   * mas los correos escritos a mano. Y se guardan las areas VACIAS para poder decirlo. */
  const prepararReenvio = () => {
    const todasAreas = [...(d.areas_derivadas || []), ...(d.areas_elegidas || [])];
    const personas = new Set();
    todasAreas.forEach((a) => (a.miembros || []).forEach((m) => personas.add(m.id)));
    const correos = (d.correos_elegidos || []).length;
    setConfReenvio({
      todos: false,
      personas: personas.size,
      correos,
      total: personas.size + correos,
      vacias: todasAreas.filter((a) => !(a.miembros || []).length).map((a) => a.nombre),
      ya: (d.avisados || []).length,
    });
  };

  const confirmarReenvio = async () => {
    const todos = !!(confReenvio && confReenvio.todos);
    setConfReenvio(null);
    await accion('reenvio', async () => {
      const r = await renotificarBaja(d.id, todos);
      /* Se dice lo que PASO, no «hecho». Se eligio `compuestos` y no `enviados` porque en local y
       * en TEST el correo es simulacro y `enviados` vale 0 siempre — un mensaje basado en el diria
       * «no se aviso a nadie» justo donde se esta probando. **Esa razon sigue valiendo.**
       *
       * LO QUE FALTABA ES SEPARAR SIMULACRO DE BLOQUEO. Compuesto no es salido:
       *
       *   simulado   el gate estaba en mock   «habria salido»   -> info, y SI quedan apuntados
       *   bloqueado  la allowlist lo paro     «NO salio»        -> ERROR, y NO quedan apuntados
       *
       * Antes los dos caian en el mismo `toast.success` con el numero de compuestos, asi que un
       * envio bloqueado al 100% salia en verde y con cifra. Paso en produccion, con BAJ-2026-0002. */
      const n = (x) => Number((r && r[x]) || 0);
      if (r && r.motivo === 'todos_ya_avisados') {
        toast.info('Ya estaban avisados todos. Marca «a todos» si quieres repetirlo.');
      } else if (r && r.motivo === 'areas_sin_miembros') {
        const nombres = (r.areas_sin_miembros || []).map((a) => a.nombre).join(', ');
        toast.warn(`No hay a quién avisar: ${nombres || 'las áreas implicadas'} no tiene(n) miembros. `
          + 'Añádelos en Incorporaciones → Áreas, o escribe aquí un correo suelto.');
      } else if (r && r.motivo === 'sin_destinatarios') {
        toast.warn('No hay a quién avisar: ninguna tarea tiene área ni responsable, '
          + 'y no has añadido áreas ni correos.');
      } else if (n('bloqueados') > 0 && n('enviados') === 0 && n('simulados') === 0) {
        toast.error(`No salió ningún correo: los ${n('bloqueados')} destinatario(s) quedaron `
          + 'bloqueados por la lista de pruebas, y NO quedan como avisados.');
      } else if (n('enviados') > 0) {
        toast.success(`Aviso enviado a ${n('enviados')} destinatario(s).`
          + (n('bloqueados') ? ` Otros ${n('bloqueados')} quedaron bloqueados y NO se les ha avisado.` : ''));
      } else if (n('simulados') > 0) {
        toast.info(`Aviso preparado para ${n('simulados')} destinatario(s). En este entorno el correo `
          + 'es un simulacro: no ha salido, pero quedan como avisados.');
      } else {
        toast.warn('No se envió nada. Mira el detalle en el registro del servidor.');
      }
      return r;
    });
  };

  const abrirCierre = async () => {
    setCargandoPrevio(true);
    try {
      setPrevio(await cierrePrevioBaja(bajaId));
    } catch (e) {
      toast.error(cuerpoError(e).message || 'No se pudo preparar el cierre.');
    } finally {
      setCargandoPrevio(false);
    }
  };

  const confirmarCierre = async (confirmaciones) => {
    setEnVuelo('cerrar');
    try {
      const r = await cerrarBaja(bajaId, confirmaciones);
      setPrevio(null);
      await trasAccion(r.desactivada_ahora
        ? `Baja cerrada. ${r.titular?.nombre || 'La persona'} ya no puede entrar en la intranet.`
        : 'Baja cerrada.');
    } catch (e) {
      // El servidor es la barrera real: si devuelve un 409 de guarda, se recarga el previo para
      // que la confirmación muestre lo que falta en vez de un error suelto.
      const c = cuerpoError(e);
      toast.error(c.message || 'No se pudo cerrar la baja.');
      if (c.error === 'fecha_futura' || c.error === 'tareas_pendientes') {
        try { setPrevio(await cierrePrevioBaja(bajaId)); } catch (_) { /* se queda el que había */ }
      }
    } finally {
      setEnVuelo(null);
    }
  };

  const abierta = !!d && ['borrador', 'en_curso'].includes(d.estado);
  /* AVISAR NO SIGUE A `abierta`, y es lo unico de esta pantalla que no lo hace.
   *
   * El servidor solo rechaza las CANCELADAS, con su razon escrita: «el aviso habla del trabajo
   * que queda tras la baja, y ese sigue existiendo despues de cerrarla». La pantalla era mas
   * estricta que el servidor, y eso dejaba el aviso de una baja cerrada alcanzable SOLO por API.
   *
   * Caso real: BAJ-2026-0001 se cerro sin que su aviso saliera —`INCORP_EMAIL_REAL` no estaba
   * definida— y recuperarlo es justo esto. El aviso que mas falta hacia era el de una cerrada.
   *
   * EDITAR QUIÉN SE ENTERA TAMPOCO SIGUE A `abierta`, por lo mismo: el servidor (`editarAreas`)
   * sólo rechaza las canceladas, y añadir a quien faltara es justo lo que pide una cerrada. Aquí
   * decía lo contrario, y esa frase se copió al mockup; se corrigió el 14/10 midiendo el servidor.
   *
   * Lo que SÍ sigue atado a `abierta` en esta pantalla es el trabajo: añadir y quitar tareas,
   * marcarlas y revocar. OJO, el servidor NO es igual de estricto en todo (medido el 14/10):
   * rechaza AÑADIR tareas en una cerrada, pero revocar sólo lo rechaza en una cancelada, y marcar o
   * quitar una tarea no miran el estado de la baja. La pantalla era así antes de esta fase, y la
   * fase no toca reglas (§1): se deja igual, y la diferencia está anotada aparte. */
  const puedeAvisar = !!d && d.estado !== 'cancelada';
  const pendientes = useMemo(() => (d?.tareas || []).filter((t) => t.estado !== 'completada'), [d]);
  const todoHecho = !!d && d.progreso?.total > 0 && pendientes.length === 0;

  /* ── ACCESO ↔ TAREA, EN LA MISMA FILA ────────────────────────────────────────────────────────
   *
   * Una tarea está ENLAZADA a un acceso cuando lleva su `recurso_id` (el servidor la completa al
   * revocar ese acceso: `completarTareaDelRecurso`). Esas van en la fila de su acceso, y así no hace
   * falta ninguna frase que lo explique: revocar cambia las dos celdas, y marcar la tarea sólo la
   * suya. Si el acceso sigue activo con su tarea hecha, la fila sale en rojo.
   *
   * Medido en producción el 14/10: ningún acceso tiene más de una tarea en una misma baja. Aun así
   * la celda pinta TODAS las enlazadas, para que una segunda no desaparezca si un día existe.
   *
   * Las demás tareas van en «Otras tareas», incluidas las que HABLAN de un acceso sin estar
   * enlazadas (anteriores al enlace). Ésa es la única relación que la disposición no puede enseñar,
   * porque en los datos no existe, y por eso es la única que se sigue diciendo con palabras. */
  const creds = d?.tiene?.credenciales || [];
  const recursosConFila = new Set(creds.map((c) => c.recurso_id).filter((x) => x != null).map(Number));
  const tareasDelAcceso = (cr) => (d?.tareas || [])
    .filter((t) => t.recurso_id != null && cr.recurso_id != null && Number(t.recurso_id) === Number(cr.recurso_id));
  const tareasEnFila = (d?.tareas || []).filter((t) => t.recurso_id != null && recursosConFila.has(Number(t.recurso_id)));
  /* MATERIAL ↔ TAREA, EN LA MISMA FILA (18/10). Igual que los accesos: una tarea con pieza (`con_pieza`)
   * va en la fila de su pieza, en «Material», y no en «Otras tareas». Las de material SIN pieza
   * (anteriores al 18/10) se quedan en «Otras» con su etiqueta, como los accesos viejos. */
  const tareasMaterial = (d?.tareas || []).filter((t) => t.con_pieza);
  const otrasTareas = (d?.tareas || []).filter((t) => !t.con_pieza && !(t.recurso_id != null && recursosConFila.has(Number(t.recurso_id))));
  const hechas = (lista) => lista.filter((t) => t.estado === 'completada').length;

  /* ¿LO QUE SE ESCRIBE REPITE UNA TAREA QUE YA ESTÁ? (18/10). Una tarea a mano que duplica la de una
   * pieza o un acceso suma al total y el cierre la pide como pendiente. Se AVISA, no se impide: puede
   * ser otra cosa («el portátil del almacén, no el suyo»). Ver `parecidoATarea`. */
  const yaConTarea = [
    ...((d?.material?.con_tarea) || []).map((f) => ({ etiqueta: `${f.pieza.tipo_nombre || 'Pieza'}${f.pieza.referencia ? ` · ${f.pieza.referencia}` : ''}`, textos: [f.pieza.tipo_nombre, f.pieza.referencia], donde: 'Material' })),
    ...creds.filter((cr) => tareasDelAcceso(cr).length).map((cr) => ({ etiqueta: cr.nombre, textos: [cr.nombre], donde: 'Accesos' })),
  ];
  const repiteA = parecidos(nuevaTarea, yaConTarea);

  /* LAS TRES PARTES DEL TOTAL (eran dos hasta el 18/10). Son una partición de `d.tareas` —cada tarea cae en una y sólo una—,
   * así que suman exactamente el `d.progreso` del servidor: no hay un segundo cálculo que pueda
   * discrepar del primero, sólo el mismo repartido en dos. */
  const COLOR_ACCESOS = '#0066b3';
  const COLOR_OTRAS = '#7c3aed';
  const COLOR_MATERIAL = '#0f766e';
  const partes = [
    { clave: 'acc', nombre: 'Accesos', hechas: hechas(tareasEnFila), total: tareasEnFila.length, color: COLOR_ACCESOS },
    { clave: 'mat', nombre: 'Material', hechas: hechas(tareasMaterial), total: tareasMaterial.length, color: COLOR_MATERIAL },
    { clave: 'tar', nombre: 'Otras tareas', hechas: hechas(otrasTareas), total: otrasTareas.length, color: COLOR_OTRAS },
  ];

  /* Quién está avisado ya. El detalle trae `avisados` como `{ id, nombre }`: con `id` si es una
   * persona, y con `id: null` y el correo en `nombre` si es un correo de fuera. */
  const avisadosIds = new Set((d?.avisados || []).filter((x) => x.id != null).map((x) => Number(x.id)));
  const avisadosCorreos = new Set((d?.avisados || []).filter((x) => x.id == null).map((x) => String(x.nombre).toLowerCase()));
  const estaAvisado = (m) => avisadosIds.has(Number(m.id));
  const correoAvisado = (c) => avisadosCorreos.has(String(c).toLowerCase());
  const sinAvisar = (() => {
    const personas = new Set();
    [...(d?.areas_derivadas || []), ...(d?.areas_elegidas || [])]
      .forEach((a) => (a.miembros || []).forEach((m) => { if (!estaAvisado(m)) personas.add(Number(m.id)); }));
    return personas.size + (d?.correos_elegidos || []).filter((c) => !correoAvisado(c)).length;
  })();

  const revocar = (cr) => {
    const deducida = cr.origen === 'deducida';
    const clave = deducida ? `r${cr.recurso_id}` : `c${cr.id}`;
    return accion(`cred-${clave}`, async () => {
      const r = deducida
        ? await revocarCredencialDeducidaBaja(d.id, cr.recurso_id)
        : await revocarCredencialBaja(d.id, cr.id);
      if (r && r.tarea_sin_enlace) {
        toast.warn(`Revocado. Su tarea «${r.tarea_sin_enlace.descripcion}» `
          + 'sigue pendiente: es anterior al enlace y hay que marcarla a mano.');
      } else {
        toast.success(`Acceso «${cr.nombre}» revocado`
          + (r && r.tarea_completada ? ' y su tarea, completada.' : '.'));
      }
      return r;
    });
  };
  /* Deshacer un clic equivocado. Borra la fila que creó la revocación (la deducción vuelve) y, si
   * ese mismo clic completó la tarea, la devuelve a pendiente. Se dice lo que pasó con la tarea:
   * quien deshace espera verla volver, y si la había marcado alguien a mano no vuelve. */
  const deshacer = (cr) => accion(`cred-c${cr.id}`, async () => {
    const r = await deshacerRevocacionBaja(d.id, cr.id);
    toast.success(`Revocación de «${cr.nombre}» deshecha: vuelve a estar activo`
      + (r && r.tareas_devueltas ? ' y su tarea, pendiente.' : '.'));
    return r;
  });
  /* Marcar una tarea de material LO HACE en Inventario (18/10): devuelve la pieza o, si es una
   * licencia, la libera. Lo que no se pudo hacer —la pieza ya no existe, está dada de baja, ya no era
   * suya— lo dice el servidor en `notas`, y aquí se enseña: si no, parecería hecho. */
  const ciclarTarea = (t) => accion(`tarea-${t.id}`, async () => {
    const r = await cambiarEstadoTareaBaja(d.id, t.id, CICLO[t.estado] || 'pendiente');
    ((r && r.notas) || []).forEach((n) => toast.info(n, { autoClose: 9000 }));
    return r;
  });

  const estadoBadge = d ? (() => {
    const m = ESTADO_BAJA_MAP[d.estado] || { tone: 'info', label: d.estado };
    return <Badge tone={m.tone}>{m.label}</Badge>;
  })() : null;

  // ── cabecera de marca ──
  // El progreso YA NO va aquí: va en la banda del total, junto a lo que suma.
  const header = (
    <div className="bg-v25-brand-800 px-5 py-4 text-white">
      {!d ? <div className="h-16" /> : (
        <div className="flex items-start gap-3">
          <Avatar name={d.titular?.nombre || 'Persona'} size="lg" className="h-14 w-14 text-sm ring-4 ring-white/15" />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="truncate text-xl font-semibold">{d.titular?.nombre || 'Sin persona'}</h1>
              {estadoBadge}
              <span className="font-mono text-xs text-white/70">{d.numero}</span>
            </div>
            {/* EL DATO QUE DEFINE UNA BAJA: si esa persona puede entrar o no. */}
            <div className="mt-1.5 flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-white/80">
              <span className="flex items-center gap-1">
                <Calendar className="h-3.5 w-3.5" />
                {d.fecha_baja ? `Baja el ${fmtFecha(d.fecha_baja)}` : 'Sin fecha de baja'}
              </span>
              {d.titular && (
                <span className={`flex items-center gap-1 ${d.titular.activo ? 'text-amber-200' : ''}`}>
                  {d.titular.activo
                    ? <><UserCheck className="h-3.5 w-3.5" />Todavía con acceso</>
                    : <><ShieldOff className="h-3.5 w-3.5" />Sin acceso a la intranet</>}
                </span>
              )}
              {/* Una baja CANCELADA puede no tener motivo —su texto se descartó al retirar la
                  columna, a propósito— y entonces no se pinta nada. */}
              {d.motivo_nombre && <span className="truncate" title={d.motivo_nombre}>Motivo: {d.motivo_nombre}</span>}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // ── la barra de acciones: TODAS, con forma de botón ──
  const barra = ({ aLaFicha }) => (!d ? null : (
    <BarraAcciones
      izquierda={(
        <>
          {abierta && (
            <Button variant="outline" onClick={() => setEditarAbierto(true)}><Pencil />Editar fecha y motivo</Button>
          )}
          {/* EN UNA CERRADA SE CORRIGE EL MOTIVO, y sólo eso: la fecha no. El rótulo es otro a
              propósito: aquí no se edita la baja, se corrige un dato. */}
          {d.estado === 'completada' && (
            <Button variant="outline" onClick={() => setEditarAbierto(true)}><Pencil />Corregir el motivo</Button>
          )}
          {puedeAvisar && (
            <Button variant="outline" onClick={prepararReenvio} disabled={enVuelo === 'reenvio'}>
              {enVuelo === 'reenvio' ? <Loader2 className="animate-spin" /> : <Mail />}Volver a avisar
              {sinAvisar > 0 && (
                <span className="ml-1 rounded-v25-full bg-amber-500 px-1.5 text-[11px] font-medium text-white">{sinAvisar} sin avisar</span>
              )}
            </Button>
          )}
          {aLaFicha && (
            <Button variant="outline" onClick={aLaFicha}><UserPen />Datos de la persona</Button>
          )}
        </>
      )}
      derecha={
        abierta ? (
          <Button variant={todoHecho ? 'default' : 'outline'} onClick={abrirCierre}
            disabled={cargandoPrevio || enVuelo === 'cerrar'}>
            {cargandoPrevio ? <Loader2 className="animate-spin" /> : <CheckCircle2 />}Cerrar la baja
            {pendientes.length > 0 && <span className="ml-1 text-xs opacity-75">· quedan {pendientes.length}</span>}
          </Button>
        ) : d.estado === 'cancelada' ? (
          <span className="flex items-center gap-2 text-sm text-v25-expired-fg"><Ban className="h-4 w-4" />Baja cancelada</span>
        ) : (
          <span className="flex items-center gap-2 text-sm text-v25-active-fg"><CheckCircle2 className="h-4 w-4" />Baja cerrada</span>
        )
      }
    />
  ));

  return (
    <>
      <PantallaProceso
        rutaListado={RUTA_BAJAS}
        persona={d && d.titular ? { ambito: d.ambito, id: d.titular.id } : null}
        header={header}
        barra={barra}
        franjaFina={d ? <FranjaFina nombre={d.titular?.nombre || 'Sin persona'} numero={d.numero} estado={estadoBadge} /> : null}
      >
        {cargando ? (
          <div className="flex items-center justify-center gap-2 py-16 text-sm text-v25-ink-500">
            <Loader2 className="h-5 w-5 animate-spin" /> Cargando…
          </div>
        ) : error === 'no_existe' ? (
          <div className="px-5 py-12 text-center text-sm text-v25-ink-700">
            <AlertCircle className="mx-auto mb-2 h-6 w-6 text-v25-ink-400" />No existe la baja #{bajaId}.
          </div>
        ) : error || !d ? (
          <div className="px-5 py-12 text-center text-sm text-red-600">
            <AlertCircle className="mx-auto mb-2 h-6 w-6" />No se pudo cargar el detalle de la baja.
            <div><Button variant="outline" className="mt-3" onClick={cargar}>Reintentar</Button></div>
          </div>
        ) : (
          <div className="proc-rejilla-baja">
            <div className="proc-total"><BandaTotal verbo="Hecho" partes={partes} /></div>

            {/* ── ACCESOS, CADA UNO CON SU TAREA ── */}
            <section className="rounded-v25-lg border border-v25-line bg-white">
              <CabeceraColumna clave="acc" color={COLOR_ACCESOS} icono={KeyRound} titulo="Accesos"
                hechas={partes[0].hechas} total={partes[0].total} />
              {creds.length === 0 ? (
                <div className="p-4"><Vacio>No consta ninguna cuenta a su nombre.</Vacio></div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead className="text-[11px] uppercase tracking-wide text-v25-ink-400">
                      <tr>
                        <th className="px-4 pt-2.5 font-medium">Acceso</th>
                        <th className="px-4 pt-2.5 font-medium">Estado</th>
                        <th aria-label="Acción" />
                        <th aria-hidden="true" />
                        <th className="px-4 pt-2.5 font-medium">Su tarea</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-v25-line">
                      {creds.map((cr) => {
                        const deducida = cr.origen === 'deducida';
                        // La clave no puede ser `cr.id`: las deducidas no tienen fila y su id es null.
                        const clave = deducida ? `r${cr.recurso_id}` : `c${cr.id}`;
                        const ocupada = enVuelo === `cred-${clave}`;
                        const suyas = tareasDelAcceso(cr);
                        const activo = !!cr.pendiente_revocar;
                        /* DOS DESFASES, DOS COLORES, porque no pesan lo mismo:
                         *   rojo   tarea hecha y el acceso SIGUE ACTIVO: alguien cree que está hecho
                         *          y no lo está. Es el grave.
                         *   ámbar  acceso REVOCADO y su tarea sin hacer: el hecho ocurrió y el
                         *          registro lo niega. Es un registro desfasado, no un riesgo.
                         * Revocar es el hecho y la tarea su registro: desmarcar la tarea no
                         * devuelve el acceso (cambiarEstadoTarea sólo toca la tarea, medido). */
                        const desfase = activo && suyas.length > 0 && suyas.every((t) => t.estado === 'completada');
                        const registroAtrasado = !activo && suyas.some((t) => t.estado !== 'completada');
                        return (
                          <tr key={clave} className={desfase ? 'bg-red-50/70' : registroAtrasado ? 'bg-amber-50/70' : ''}>
                            <td className="px-4 py-2.5 align-top">
                              <div className="flex items-center gap-1.5">
                                <span className="text-sm font-medium text-v25-ink-900">{cr.nombre}</span>
                                {/* Deducir está bien, pero hay que decir que se ha deducido. */}
                                {deducida && <Badge tone="info" icon={Sparkles} className="shrink-0">Deducida</Badge>}
                              </div>
                              <div className="text-xs text-v25-ink-400">
                                {cr.valor || (deducida ? 'sin identificador guardado' : '—')}
                                {deducida && cr.senal && <span className="ml-1 text-v25-ink-300">· de {cr.senal.join(', ')}</span>}
                              </div>
                            </td>
                            {activo ? (
                              <>
                                <td className="px-4 py-2.5 align-top">
                                  <Badge tone={desfase ? 'expired' : 'waiting'}>Sigue activo</Badge>
                                </td>
                                <td className="px-2 py-2.5 align-top">
                                  {abierta && (
                                    <Button size="sm" variant="outline" disabled={ocupada} onClick={() => revocar(cr)}
                                      className="border-red-200 text-red-700 hover:bg-red-50">
                                      {ocupada ? <Loader2 className="animate-spin" /> : <ShieldOff />}Revocar
                                    </Button>
                                  )}
                                </td>
                              </>
                            ) : (
                              /* QUIÉN Y CUÁNDO, Y QUE NO SE DESHACE. Una revocación es definitiva: el
                                 servidor rechaza cualquier cambio sobre una credencial revocada
                                 (credencialesService, «ya_revocada»). Por eso no hay botón, y la fila
                                 lo dice y da la salida, que es lo que resuelve un clic donde no tocaba:
                                 registrar un acceso NUEVO en su ficha. El enlace abre otra pestaña,
                                 como los «→ Ficha» del alta, para no perder la baja.
                                 Ocupa las DOS celdas (estado y acción): en una fila revocada la del
                                 botón está vacía, y con las dos cabe sin ensanchar la tabla, que en un
                                 portátil echaba «Su tarea» fuera de la vista. */
                              <td colSpan={2} className="px-4 py-2.5 align-top">
                                <Badge tone="active" icon={ShieldOff}>Revocado</Badge>
                                {(cr.revocada_en || cr.revocada_por_nombre) && (
                                  <div className="mt-1 text-[11px] text-v25-ink-500">
                                    {cr.revocada_en && <span className="whitespace-nowrap">{fmtMomento(cr.revocada_en)}</span>}
                                    {cr.revocada_en && cr.revocada_por_nombre && ' · '}
                                    {cr.revocada_por_nombre}
                                  </div>
                                )}
                                {/* DESHACER, donde se puede sin inventar nada: si era deducida, la
                                    revocación creó la fila y borrarla devuelve el estado de antes.
                                    Lo decide el servidor (`deshacible`), con la misma regla que la
                                    acción. Donde no se puede, se dice por qué al pasar el ratón. */}
                                {cr.deshacible && abierta ? (
                                  <Button size="sm" variant="outline" className="mt-1.5" disabled={ocupada}
                                    onClick={() => deshacer(cr)}
                                    title="Si «Revocar» se pulsó por error: el acceso vuelve a estar activo y, si este clic completó su tarea, la tarea vuelve a pendiente.">
                                    {ocupada ? <Loader2 className="animate-spin" /> : <Undo2 />}Deshacer
                                  </Button>
                                ) : (
                                  <div className="text-[11px] text-v25-ink-400"
                                    title={!abierta
                                      ? 'La baja ya no está abierta: deshacer trabajo después de cerrarla sería reabrirla.'
                                      : 'Este acceso existía antes de revocarlo (viene de un alta o se registró a mano), y deshacer tendría que inventar en qué estado estaba.'}>
                                    No se deshace
                                  </div>
                                )}
                              </td>
                            )}
                            <td className="px-1 py-2.5 align-top text-v25-ink-300" aria-hidden="true"><ArrowRight className="h-4 w-4" /></td>
                            <td className="px-4 py-2.5 align-top">
                              {suyas.length === 0 ? (
                                <span className="text-xs text-v25-ink-400" title="Este acceso no tiene tarea en esta baja">—</span>
                              ) : (
                                <div className="flex flex-col items-start gap-1">
                                  {suyas.map((t) => (
                                    <EstadoTarea key={t.id} estado={t.estado} disabled={!abierta}
                                      ocupada={enVuelo === `tarea-${t.id}`} onClick={() => ciclarTarea(t)}
                                      etiqueta={`Cambiar el estado de: ${t.descripcion}`} />
                                  ))}
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              {/* EL LÍMITE, DICHO. Esta frase se queda: no explica una relación de la pantalla,
                  dice lo que el módulo no puede saber solo. */}
              {(d.tiene?.credenciales_no_deducibles || []).length > 0 && (
                <p className="flex items-start gap-1.5 border-t border-v25-line px-4 py-2.5 text-xs leading-relaxed text-v25-ink-400">
                  <HelpCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <span>
                    {d.tiene.credenciales_no_deducibles.length} acceso(s) del catálogo no se pueden
                    comprobar solos porque no están en ninguna columna de la ficha:{' '}
                    <strong className="font-medium text-v25-ink-500">
                      {d.tiene.credenciales_no_deducibles.map((x) => x.nombre).join(', ')}
                    </strong>. Si los tenía, añádelos como tarea.
                  </span>
                </p>
              )}
            </section>

            {/* ── OTRAS TAREAS ── */}
            <section className="rounded-v25-lg border border-v25-line bg-white">
              <CabeceraColumna clave="tar" color={COLOR_OTRAS} icono={ListChecks} titulo="Otras tareas"
                hechas={partes[2].hechas} total={partes[2].total} />
              {otrasTareas.length === 0 ? (
                <div className="p-4"><Vacio>Ninguna.{abierta ? ' Puedes añadir las que hagan falta aquí abajo.' : ''}</Vacio></div>
              ) : (
                <ul className="divide-y divide-v25-line">
                  {otrasTareas.map((t) => {
                    const ocupada = enVuelo === `tarea-${t.id}`;
                    return (
                      <li key={t.id} className="flex items-center gap-3 px-4 py-2.5">
                        <div className="min-w-0 flex-1">
                          <div className={`text-sm ${t.estado === 'completada' ? 'text-v25-ink-400 line-through' : 'text-v25-ink-900'}`}>{t.descripcion}</div>
                          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-v25-ink-400">
                            {t.area_nombre && <span>{t.area_nombre}</span>}
                            {t.responsable_nombre && <span>· {t.responsable_nombre}</span>}
                            {/* LA ÚNICA RELACIÓN QUE SE SIGUE DICIENDO CON PALABRAS: habla de un
                                acceso pero no está enlazada con él, así que la disposición no la
                                puede enseñar. En la fila, corta, y el porqué al pasar el ratón. */}
                            {/* Material sin pieza: anterior al enlace del 18/10. Lo mismo que el acceso
                                viejo de abajo: la disposición no lo puede enseñar, así que se dice. */}
                            {t.es_de_material && !t.con_pieza && t.estado !== 'completada' && (
                              <span className="inline-flex items-center gap-1 rounded-v25-full border border-v25-waiting-bd bg-v25-waiting-bg px-1.5 text-[11px] text-v25-waiting-fg"
                                title="Es anterior al 18/10: marcarla no toca Inventario. La pieza hay que devolverla allí.">
                                <Unlink className="h-3 w-3" />sin enlace a su pieza
                              </span>
                            )}
                            {t.es_de_acceso && !t.enlazada && t.estado !== 'completada' && (
                              <span className="inline-flex items-center gap-1 rounded-v25-full border border-v25-waiting-bd bg-v25-waiting-bg px-1.5 text-[11px] text-v25-waiting-fg"
                                title="Es anterior al enlace entre tareas y accesos: revocar el acceso no la marca. Hay que marcarla a mano.">
                                <Unlink className="h-3 w-3" />sin enlace a un acceso
                              </span>
                            )}
                          </div>
                        </div>
                        <EstadoTarea estado={t.estado} disabled={!abierta} ocupada={ocupada}
                          onClick={() => ciclarTarea(t)} etiqueta={`Cambiar el estado de: ${t.descripcion}`} />
                        {abierta && (
                          <Button variant="ghost" size="icon" aria-label={`Quitar tarea: ${t.descripcion}`}
                            disabled={ocupada || t.estado === 'completada'}
                            title={t.estado === 'completada' ? 'Una tarea hecha no se quita: desmárcala primero' : 'Quitar tarea'}
                            onClick={() => accion(`tarea-${t.id}`, () => eliminarTareaBaja(d.id, t.id), 'Tarea quitada.')}
                            className="shrink-0 text-red-600 hover:bg-red-50 hover:text-red-700"><Trash2 /></Button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
              {/* AÑADIR A MANO: la contrapartida del aviso de «no fiable». */}
              {abierta && (
                <form className="flex items-end gap-2 border-t border-v25-line px-4 py-2.5"
                  onSubmit={(e) => {
                    e.preventDefault();
                    const txt = nuevaTarea.trim();
                    if (!txt) return;
                    accion('nueva-tarea', async () => {
                      await crearTareaBaja(d.id, { descripcion: txt });
                      setNuevaTarea('');
                    }, 'Tarea añadida.');
                  }}>
                  <Input wrapperClassName="flex-1" label="Añadir una tarea" placeholder="Recoger la llave del almacén…"
                    value={nuevaTarea} maxLength={200} onChange={(e) => setNuevaTarea(e.target.value)} />
                  <Button type="submit" variant="outline" disabled={!nuevaTarea.trim() || enVuelo === 'nueva-tarea'}>
                    {enVuelo === 'nueva-tarea' ? <Loader2 className="animate-spin" /> : <Plus />}Añadir
                  </Button>
                </form>
              )}
              {abierta && repiteA.length > 0 && (
                <p className="flex items-start gap-1.5 px-4 pb-2.5 text-[11px] text-v25-waiting-fg" role="status">
                  <Info className="mt-px h-3.5 w-3.5 shrink-0" />
                  <span>Ya hay una tarea para {repiteA.map((x, i) => <React.Fragment key={x.etiqueta}>{i ? ', ' : ''}<strong>«{x.etiqueta}»</strong> en {x.donde}</React.Fragment>)}: ¿es la misma? Si lo es, se sumaría al total y habría que hacerla dos veces. Si es otra cosa, añádela igual.</span>
                </p>
              )}
            </section>

            <div className="proc-lateral">
              {/* ── MATERIAL ── cada pieza CON su tarea (18/10), como cada acceso con la suya. Las filas
                  salen de las tareas con pieza: al marcarla y devolverla, la fila sigue y dice «devuelta».
                  Y las dos direcciones: hecha e Inventario diciendo que la tiene (rojo), y una pieza a su
                  nombre sin tarea —se le asignó después de crear la baja— con el botón para crearla. */}
              <section className="rounded-v25-lg border border-v25-line bg-white">
                <CabeceraColumna clave="mat" color={COLOR_MATERIAL} icono={Package} titulo="Material"
                  hechas={partes[1].hechas} total={partes[1].total} />
                <div className="p-4">
                  {d.material && d.material.fiable === false ? (
                    // El texto del BACKEND, tal cual: «no tiene» y «no lo sabemos» no son lo mismo.
                    <div className="flex gap-2 rounded-v25-lg border border-amber-200 bg-amber-50 px-3 py-2.5">
                      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                      <div className="min-w-0">
                        <p className="text-xs font-semibold text-amber-900">No se puede saber qué material tiene</p>
                        <p className="mt-0.5 text-xs leading-relaxed text-amber-800">{d.tiene?.material?.motivo_no_fiable}</p>
                      </div>
                    </div>
                  ) : !(d.material?.con_tarea?.length) && !(d.material?.sin_tarea?.length) ? (
                    <Vacio>Ni tareas de material ni piezas a su nombre en Inventario.</Vacio>
                  ) : (
                    <ul className="space-y-1.5">
                      {(d.material?.con_tarea || []).map((f) => {
                        const tarea = (d.tareas || []).find((x) => x.id === f.tarea_id);
                        const INV = {
                          suya: 'Inventario: a su nombre',
                          devuelta: `Inventario: devuelta${f.inventario_fecha ? ` el ${fmtFecha(f.inventario_fecha)}` : ''}`,
                          de_otro: 'Inventario: ya no está a su nombre',
                          de_baja: 'Inventario: dada de baja',
                          no_existe: 'Inventario: la pieza ya no existe',
                          sin_tipo: 'Inventario: su tipo ya no existe',
                        };
                        return (
                          <li key={`t${f.tarea_id}`} className={`flex items-center gap-2 rounded-v25-md border px-3 py-2 ${f.contradice ? 'border-red-300 bg-red-50' : 'border-v25-line'}`}>
                            <div className="min-w-0 flex-1">
                              <div className="truncate text-sm text-v25-ink-900">{f.pieza.tipo_nombre || 'Pieza'}{f.pieza.referencia ? <span className="text-v25-ink-400"> · {f.pieza.referencia}</span> : null}</div>
                              <div className={`text-[11px] ${f.contradice ? 'font-medium text-red-700' : 'text-v25-ink-500'}`}>
                                {f.contradice ? 'Hecha, pero Inventario dice que la sigue teniendo' : INV[f.inventario]}
                                {f.area_nombre ? <span className="text-v25-ink-400"> · {f.area_nombre}</span> : null}
                              </div>
                            </div>
                            {tarea && (
                              <EstadoTarea estado={tarea.estado} disabled={!abierta} ocupada={enVuelo === `tarea-${tarea.id}`}
                                onClick={() => ciclarTarea(tarea)} etiqueta={`Cambiar el estado de: ${tarea.descripcion}`} />
                            )}
                          </li>
                        );
                      })}
                      {(d.material?.sin_tarea || []).map((m) => (
                        <li key={`s${m.pieza.tipo_id}-${m.pieza.asset_id}`} className="flex items-center gap-2 rounded-v25-md border border-amber-200 bg-amber-50 px-3 py-2">
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-sm text-v25-ink-900">{m.pieza.tipo_nombre}{m.pieza.referencia ? <span className="text-v25-ink-400"> · {m.pieza.referencia}</span> : null}</div>
                            <div className="text-[11px] text-amber-800">A su nombre en Inventario y sin tarea: se le asignó después de crear la baja.</div>
                          </div>
                          {abierta && (
                            <Button size="sm" variant="outline" disabled={enVuelo === `mat-${m.pieza.asset_id}`}
                              onClick={() => accion(`mat-${m.pieza.asset_id}`, () => crearTareaMaterialBaja(d.id, m.pieza.tipo_id, m.pieza.asset_id), 'Tarea añadida.')}>
                              <Plus />Añadir su tarea
                            </Button>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </section>

              <QuienSeEntera
                derivadas={d.areas_derivadas || []}
                elegidas={d.areas_elegidas || []}
                correos={d.correos_elegidos || []}
                estaAvisado={estaAvisado}
                correoAvisado={correoAvisado}
                editable={puedeAvisar}
                ocupada={enVuelo === 'areas'}
                catalogo={areasCatalogo}
                areaAAvisar={areaAAvisar}
                setAreaAAvisar={setAreaAAvisar}
                onAnadirArea={() => guardarDestinatarios([...listaActual(), { area_id: Number(areaAAvisar) }])}
                onQuitarArea={(a) => guardarDestinatarios(listaActual().filter((x) => x.area_id !== a.id))}
                correoAAvisar={correoAAvisar}
                setCorreoAAvisar={(v) => { setCorreoAAvisar(v); setErrorCorreo(null); }}
                errorCorreo={errorCorreo}
                onAnadirCorreo={anadirCorreo}
                onQuitarCorreo={(c) => guardarDestinatarios(listaActual().filter((x) => x.email !== c))}
              />
            </div>
          </div>
        )}
      </PantallaProceso>

      {/* El MISMO diálogo que usa el banner de la ficha. Va FUERA de la pantalla: con el Drawer
          esto decidía quién pintaba encima (los dos eran `z-50`); ahora los diálogos son portales
          `z-50` y lo único con z-index aquí es el pie fijo, `z-10`. */}
      {d && (
        <EditarBajaDialog
          open={editarAbierto}
          /* `estado`, `motivo_id` y `motivo` viajan los tres: el dialogo necesita saber si la
             baja esta cerrada, cual es el motivo del catalogo, y —si no lo hay— que texto antiguo
             quedo sin mapear para poder ensenarlo. */
          baja={{ id: d.id, numero: d.numero, fecha_baja: d.fecha_baja, motivo: d.motivo, motivo_id: d.motivo_id, estado: d.estado }}
          personaNombre={d.titular ? d.titular.nombre : null}
          onClose={() => setEditarAbierto(false)}
          onGuardada={async () => { await cargar(); onCambio && onCambio(); }}
        />
      )}

      {/* Reenviar con los NÚMEROS delante. La casilla de «a todos» existe para el caso que la
          regla fina no cubre —alguien de un área ya avisada a quien se le ha añadido una tarea
          desde entonces—: se ofrece la decisión en vez de inventar una regla más fina. */}
      {confReenvio && (
        <Modal open onClose={() => setConfReenvio(null)} titulo="Volver a avisar">
          {/* PERSONAS Y CORREOS, no áreas. Y las áreas vacías se dicen ANTES de pulsar: un área
              sin miembros avisa a cero y hasta ahora nada lo señalaba. */}
          <p className="text-sm text-v25-ink-700">
            {confReenvio.total === 0 ? (
              <>No hay <strong>a nadie</strong> a quien escribir.</>
            ) : (
              <>
                Se escribirá a <strong>{confReenvio.total}</strong>
                {' '}destinatario(s){confReenvio.correos > 0 && (
                  <> — {confReenvio.personas} persona(s) de las áreas y {confReenvio.correos} correo(s) suelto(s)</>
                )}, más los responsables de las tareas.
              </>
            )}
          </p>
          {confReenvio.vacias.length > 0 && (
            <div className="mt-2 flex gap-2 rounded-v25-lg border border-amber-200 bg-amber-50 px-3 py-2.5">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
              <div>
                <p className="text-xs font-semibold text-amber-900">
                  {confReenvio.vacias.length === 1 ? 'Un área sin miembros' : 'Áreas sin miembros'}
                </p>
                <p className="mt-0.5 text-xs leading-relaxed text-amber-800">
                  <strong>{confReenvio.vacias.join(', ')}</strong>
                  {confReenvio.vacias.length === 1 ? ' no tiene a nadie' : ' no tienen a nadie'}, así que
                  por esa vía no se avisa a nadie. Se añaden en Incorporaciones → Áreas.
                </p>
              </div>
            </div>
          )}
          {confReenvio.ya > 0 && (
            <p className="mt-2 text-sm text-v25-ink-500">
              <strong>{confReenvio.ya}</strong> ya lo recibieron y no se les vuelve a escribir.
            </p>
          )}
          <label className="mt-3 flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={confReenvio.todos}
              onChange={(e) => setConfReenvio({ ...confReenvio, todos: e.target.checked })}
            />
            Escribir también a quienes ya lo recibieron
          </label>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="outline" onClick={() => setConfReenvio(null)}>Cancelar</Button>
            <Button onClick={confirmarReenvio}>Enviar</Button>
          </div>
        </Modal>
      )}

      {previo && (
        <ConfirmarCierre
          previo={previo}
          titular={d && d.titular}
          enVuelo={enVuelo === 'cerrar'}
          onClose={() => setPrevio(null)}
          onConfirmar={confirmarCierre}
        />
      )}
    </>
  );
}
